import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ShieldAlert, Heart, User, ClipboardList, BookOpen, ExternalLink, CheckCircle2 } from "lucide-react";
import { Waivers } from "@/components/Waivers";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const SOARING_AMAZON_URL =
  "https://www.amazon.com/dp/B0GVPGD51L?&linkCode=sl2&tag=avianayoga0b-20&linkId=39754c5c9178a30c152d18c5703ffc2b&language=en_US&ref_=as_li_ss_tl";

const DEFAULT_CANCELLATION_POLICY =
  "A non-refundable, non-transferable $350 deposit secures your spot. " +
  "Any payments beyond the deposit are refundable if written cancellation is received " +
  "at least 30 days before the program's start date. Cancellations made within 30 days " +
  "of the program will not be refunded. Aviana reserves the right to cancel any program " +
  "at its discretion — in that event, a full refund will be issued.";

const HEALTH_OPTIONS = ["Excellent", "Good", "Fair", "Poor"];

export default function RegisterTraining() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [offering, setOffering] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    payment_type: "deposit",
    full_name: "",
    email: "",
    phone: "",
    dob: "",
    address: "",
    emergency_contact_name: "",
    emergency_contact_relationship: "",
    emergency_contact_phone: "",
    health_condition: "",
    health_notes: "",
    injuries_surgeries: "",
    medications: "",
    profession: "",
    yoga_experience: "",
    why_this_training: "",
    time_commitment_agreed: null,    // null = unanswered
    hosting_studio_affiliated: null,
    cancellation_policy_agreed: null,
    additional_terms_agreed: false,
    liability_waiver_agreed: false,
    photo_release_agreed: false,
    add_book: false,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/offerings/${slug}`);
        if (data.type !== "training") {
          toast.error("Registration is only for trainings.");
          navigate(`/offering/${slug}`);
          return;
        }
        setOffering(data);
        if (!data.deposit_usd) setForm((f) => ({ ...f, payment_type: "full" }));
      } catch {
        toast.error("Training not found.");
        navigate("/");
      }
    })();
  }, [slug, navigate]);

  if (!offering) return <div className="pt-40 container-aviana text-aviana-muted">Loading…</div>;

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const validate = () => {
    const required = ["full_name", "email", "phone", "dob", "address",
      "emergency_contact_name", "emergency_contact_relationship", "emergency_contact_phone",
      "health_condition", "health_notes", "injuries_surgeries", "medications"];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        toast.error("Please fill in every required field.");
        // Scroll to first missing field
        document.querySelector(`[data-field="${k}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        return false;
      }
    }
    if (form.time_commitment_agreed !== true) {
      toast.error("Please confirm the time commitment.");
      document.querySelector('[data-field="time_commitment_agreed"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    // The "hosting studio affiliation" question only applies to trainings held
    // at partner studios (Pearl, Florida, etc.). Skip the requirement for
    // trainings without a hosting studio (Bliss Woods semi-private, etc.) and
    // trainings that have their own venue (no special_tuition rate to apply).
    const asksHostingAffiliation = !!offering?.special_tuition_usd || !!offering?.exclusive_to_studio;
    if (asksHostingAffiliation && form.hosting_studio_affiliated === null) {
      toast.error("Please answer the hosting studio affiliation question.");
      return false;
    }
    if (form.cancellation_policy_agreed !== true) {
      toast.error("Please agree to the cancellation policy to continue.");
      document.querySelector('[data-field="cancellation_policy_agreed"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    if (!form.additional_terms_agreed) {
      toast.error("Please read and agree to the additional terms & conditions.");
      document.querySelector('[data-field="additional_terms_agreed"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    if (!form.liability_waiver_agreed) {
      toast.error("Please read and agree to the release & waiver of liability.");
      document.querySelector('[data-field="liability_waiver_agreed"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    if (!form.photo_release_agreed) {
      toast.error("Please read and agree to the photo & video release waiver.");
      document.querySelector('[data-field="photo_release_agreed"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/trainings/registrations", {
        offering_id: offering.id,
        ...form,
      });
      if (data.studio_pays_tuition) {
        toast.success("Registration confirmed — your studio is handling tuition.");
        navigate(`/registration/${data.id}/confirmed`);
      } else {
        toast.success("Registration saved — let's get your payment sorted.");
        navigate(`/payment/${data.id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit registration. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const policy = offering.cancellation_policy || DEFAULT_CANCELLATION_POLICY;

  return (
    <div className="pt-28 pb-24 bg-aviana-paper" data-testid="register-training-page">
      <div className="container-aviana max-w-3xl">
        <Link to={`/offering/${slug}`} className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink mb-8">
          <ArrowLeft size={14} /> Back to offering
        </Link>

        <p className="overline-plum mb-3">Registration</p>
        <h1 className="font-display text-4xl md:text-5xl text-aviana-ink leading-tight">{offering.title}</h1>
        <p className="mt-3 text-aviana-muted text-[15px]">Please complete the form below so Holly can prepare a meaningful, safe, and personalised training experience for you.</p>

        <form onSubmit={submit} className="mt-12 space-y-12" data-testid="registration-form">
          <Section icon={User} title="Attendee Information" subtitle="So we know who's joining and how to reach you.">
            <Grid>
              <FormField label="Full name *" name="full_name" value={form.full_name} onChange={(v) => update({ full_name: v })} />
              <FormField label="Email *" name="email" type="email" value={form.email} onChange={(v) => update({ email: v })} />
              <FormField label="Phone *" name="phone" value={form.phone} onChange={(v) => update({ phone: v })} />
              <FormField label="Date of birth *" name="dob" type="date" value={form.dob} onChange={(v) => update({ dob: v })} />
            </Grid>
            <FormField label="Mailing address (street · city · state · zip) *" name="address" value={form.address} onChange={(v) => update({ address: v })} multiline rows={2} />
            <Grid>
              <FormField label="Emergency contact name *" name="emergency_contact_name" value={form.emergency_contact_name} onChange={(v) => update({ emergency_contact_name: v })} />
              <FormField label="Relationship *" name="emergency_contact_relationship" value={form.emergency_contact_relationship} onChange={(v) => update({ emergency_contact_relationship: v })} />
              <FormField label="Emergency contact phone *" name="emergency_contact_phone" value={form.emergency_contact_phone} onChange={(v) => update({ emergency_contact_phone: v })} />
            </Grid>
          </Section>

          <Section icon={Heart} title="Your Health" subtitle="So Holly can keep you safe and accommodate anything in your body. All fields are required — if none apply, please write “none” or “n/a”.">
            <div data-field="health_condition">
              <p className="overline-plum mb-3">Condition of health *</p>
              <div className="flex flex-wrap gap-2">
                {HEALTH_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => update({ health_condition: opt })}
                    className={`px-5 py-2.5 rounded-full text-[12px] tracking-[0.15em] uppercase border transition-colors ${
                      form.health_condition === opt
                        ? "bg-aviana-ink text-aviana-cream border-aviana-ink"
                        : "border-aviana-border text-aviana-inkSoft hover:border-aviana-plum"
                    }`}
                    data-testid={`health-${opt.toLowerCase()}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            <div data-field="health_notes">
              <FormField label="Existing health conditions *" placeholder='If none, write "none" or "n/a"' value={form.health_notes} onChange={(v) => update({ health_notes: v })} multiline rows={3} />
            </div>
            <div data-field="injuries_surgeries">
              <FormField label="Injuries or surgeries in the last 6 months *" placeholder='If none, write "none" or "n/a"' value={form.injuries_surgeries} onChange={(v) => update({ injuries_surgeries: v })} multiline rows={3} />
            </div>
            <div data-field="medications">
              <FormField label="Current medications *" placeholder='If none, write "none" or "n/a"' value={form.medications} onChange={(v) => update({ medications: v })} multiline rows={2} />
            </div>
          </Section>

          <Section icon={ClipboardList} title="Getting to Know You" subtitle="Holly reads every word — this helps her tailor the training to you.">
            <FormField label="What's your current profession / passion?" value={form.profession} onChange={(v) => update({ profession: v })} />
            <FormField label="Tell us about your yoga experience" value={form.yoga_experience} onChange={(v) => update({ yoga_experience: v })} multiline rows={4} />
            <FormField label="Why are you drawn to this training?" value={form.why_this_training} onChange={(v) => update({ why_this_training: v })} multiline rows={4} />
          </Section>

          <Section icon={ShieldAlert} title="Agreements" subtitle="These confirm you're ready to commit — and that you've read the cancellation policy.">
            <YesNo
              field="time_commitment_agreed"
              label="Am I committed to the time requirements? *"
              hint={
                offering.schedule_summary
                  ? `${offering.schedule_summary} Plus exam, research paper, 20 hours practice teaching, and a class audit video within 5 months. You must complete all modules to receive your certification.`
                  : `The training runs ${offering.subtitle || `${offering.start_date} – ${offering.end_date}`}. Plus exam, research paper, 20 hours practice teaching, and a class audit video within 5 months. You must complete all modules to receive your certification.`
              }
              value={form.time_commitment_agreed}
              onChange={(v) => update({ time_commitment_agreed: v })}
            />
            {(offering.special_tuition_usd || offering.exclusive_to_studio) && (
              <YesNo
                field="hosting_studio_affiliated"
                label={`Are you employed by ${offering.exclusive_studio_name || offering.venue || "the hosting studio"}? *`}
                hint={`Select Yes only if you are on staff / employed by ${offering.exclusive_studio_name || offering.venue || "the hosting studio"} (the studio hosting this training). Taking classes there does not qualify.`}
                value={form.hosting_studio_affiliated}
                onChange={(v) => update({ hosting_studio_affiliated: v })}
              />
            )}
            <div data-field="cancellation_policy_agreed" className="bg-aviana-cream/70 border border-aviana-border rounded-2xl p-6">
              <p className="overline-plum mb-3 flex items-center gap-2"><ShieldAlert size={14} /> Cancellation & refund policy</p>
              <p className="text-aviana-inkSoft text-[14px] leading-[1.75] mb-5">{policy}</p>
              <YesNo
                field="cancellation_policy_agreed_inline"
                label="I've read and agree to the cancellation policy. *"
                value={form.cancellation_policy_agreed}
                onChange={(v) => update({ cancellation_policy_agreed: v })}
                compact
              />
            </div>

            <div data-field="additional_terms_agreed" className="bg-aviana-cream/70 border border-aviana-border rounded-2xl p-6" data-testid="additional-terms-section">
              <p className="overline-plum mb-3 flex items-center gap-2"><ShieldAlert size={14} /> Additional terms & conditions</p>
              <div className="max-h-64 overflow-y-auto text-aviana-inkSoft text-[13px] leading-[1.7] space-y-3 pr-2 mb-5 border-l-2 border-aviana-gold/30 pl-4">
                <p><strong className="text-aviana-plum">Extensions.</strong> In the event that the participant is unable to complete the required modules within the 5-month timeframe allotted with this registration, the student may request an extension. There is a fee required for the extension to be granted and the student must submit payment at the time of the request before the 5-month deadline has passed. A student may request up to 2 extensions for a maximum of a 4-month total extension. If the requirements are still not met after 2 extensions, the student would be required to attend another teacher training intensive in order to receive their certification.</p>
                <p><strong>Extension fees:</strong> 1-month · $100 &nbsp; 2-month · $300 &nbsp; 3-month · $500</p>
                <p><strong className="text-aviana-plum">Commitment to the practice.</strong> By agreeing to participate in the teacher training, you represent your commitment to teaching aerial yoga and agree to uphold the yogic principles that are the basis for teaching. If for any reason Aviana deems that you are not operating in the spirit and integrity of the training or following the terms of this Agreement, you may be asked to leave the training and you will not be entitled to a refund of any portion of any amounts previously paid.</p>
                <p><strong className="text-aviana-plum">Independent teaching &amp; branding.</strong> Once you receive a final certification, you may independently teach at any location of your choosing, and you may own your own studio. You will be responsible for all costs and expenses if you breach this agreement. Your certification as an aerial yoga teacher does not make you an agent or employee of Aviana. Use of the Aviana name in conjunction with the advertisement of classes and in studios is granted on a case-by-case basis and MUST BE REQUESTED IN WRITING.</p>
                <p><strong className="text-aviana-plum">Assumption of risk &amp; release of liability.</strong> By submitting this document, you unconditionally represent that you are in good health and fully able to perform all physical requirements of the training. You understand and acknowledge that you are to receive the instruction of the training and you will not hold Aviana, or its teachers or representatives in the training, to any higher standard of care than that applicable to the standard of care required of similar yoga training and certification programs in the industry. You agree to waive and release Aviana, and its owners, agents, instructors and employees from all liability for any and all loss or damage on account of injury or death whether caused by negligence or otherwise while you are in the training or while practicing yoga or other activities, programs or education offered by Aviana. You expressly agree to hold Aviana and its owners, agents and employees harmless for any fees or costs or expenses that may be incurred out of necessity to defend any lawsuit instituted by virtue of injury or death or damage you may suffer.</p>
                <div className="bg-white/60 border border-aviana-gold/40 rounded-lg p-4 my-2">
                  <p className="text-aviana-plum font-semibold text-[12px] tracking-wider uppercase mb-2">Contraindications — please disclose any of the following before training</p>
                  <p className="text-aviana-inkSoft text-[12px] leading-[1.7]">
                    Pregnancy · Glaucoma · Recent surgery (esp. shoulder, eyes, back, hips, hands, or wrist) · Heart disease · Very high or low blood pressure · Easy onset vertigo · Osteoporosis / bone weakness · Recent head injury · Cerebral sclerosis · Propensity for fainting · Carpal tunnel syndrome · Severe arthritis · Sinusitis or head cold · Hiatal hernia · Disc herniation or acute discogenic disease · Recent stroke · Artificial hips · Radiculitis (inflammation of nerve root in spine) · Severe muscle spasms · Botox (within 6 hours)
                  </p>
                </div>
                <p><strong className="text-aviana-plum">Governing law.</strong> You agree that these provisions are intended to be as broad and inclusive as is permitted by the laws of the State of Colorado and that if any portion is held to be invalid, the balance shall, notwithstanding, continue in full force and effect. You understand and agree that this Agreement is subject to the laws of the State of Colorado and any dispute will be resolved only under the laws of the State of Colorado.</p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.additional_terms_agreed}
                  onChange={(e) => update({ additional_terms_agreed: e.target.checked })}
                  className="mt-1 accent-aviana-plum w-4 h-4"
                  data-testid="additional-terms-checkbox"
                />
                <span className="flex-1 text-[14px] text-aviana-ink leading-[1.55]">
                  I've read and agree to the additional terms &amp; conditions above (extensions policy, commitment, branding, liability release, Colorado governing law). <span className="text-aviana-plum">*</span>
                </span>
              </label>
            </div>

            <Waivers
              liabilityAgreed={form.liability_waiver_agreed}
              onLiabilityChange={(v) => update({ liability_waiver_agreed: v })}
              photoAgreed={form.photo_release_agreed}
              onPhotoChange={(v) => update({ photo_release_agreed: v })}
              testidPrefix="training-waiver"
            />
          </Section>

          <Section
            icon={BookOpen}
            title="Required text"
            subtitle="Soaring with the Soul is Holly's training text — every student needs a copy in hand for the in-person dates. If you already own it, you're set. If not, please purchase one before the training begins."
          >
            <div className="rounded-2xl border border-aviana-plum/30 bg-aviana-plum/5 p-5 space-y-4" data-testid="required-book-block">
              <p className="text-aviana-ink text-[15px] leading-[1.65]">
                <strong className="text-aviana-plum"><em>Soaring with the Soul: An Aerial Yoga Guide</em></strong> by Holly Johnson — required for all teacher trainings.
              </p>
              <a
                href={SOARING_AMAZON_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-aviana-plum text-aviana-cream text-[12px] tracking-[0.18em] uppercase hover:bg-aviana-plumDeep transition-colors"
                data-testid="purchase-book-link"
              >
                Purchase on Amazon <ExternalLink size={12} />
              </a>
              <label className="flex items-start gap-3 cursor-pointer pt-2 border-t border-aviana-plum/15">
                <input
                  type="checkbox"
                  checked={form.add_book}
                  onChange={(e) => update({ add_book: e.target.checked })}
                  className="mt-1 accent-aviana-plum w-4 h-4"
                  data-testid="already-own-book-checkbox"
                />
                <span className="flex-1 text-[13px] text-aviana-inkSoft leading-[1.55]">
                  I already own a copy, or I've ordered one — I'll have it on hand for the training.
                </span>
              </label>
            </div>
          </Section>

          {/* Payment type chooser — hidden when the hosting studio pays Holly directly */}
          {!offering.studio_pays_tuition && offering.deposit_usd && (() => {
            const eligibleForSpecial = !!offering.special_tuition_usd && form.hosting_studio_affiliated === true;
            const effectiveTuition = eligibleForSpecial ? offering.special_tuition_usd : offering.price_usd;
            const balanceLeft = effectiveTuition - offering.deposit_usd;
            const balanceTimingLabel = offering.balance_due_label
              ? offering.balance_due_label
              : (offering.balance_due_days_before === 7
                ? "1 week before the training starts"
                : `${offering.balance_due_days_before || 30} days before the training starts`);
            // Deposit-only offerings (e.g. private trainings where full tuition
            // depends on final headcount): render a locked deposit card, hide
            // the "Pay in full" option, and force payment_type=deposit.
            if (offering.deposit_only) {
              if (form.payment_type !== "deposit") update({ payment_type: "deposit" });
              return (
                <Section
                  icon={CheckCircle2}
                  title="Reserve your spot with a non-refundable deposit"
                  subtitle={`Full tuition will be confirmed once the final group size is set. Remaining balance ${balanceTimingLabel}. Balance can be paid via Zelle, Venmo, or credit card (fees apply).`}
                >
                  <div className="rounded-2xl border-2 border-aviana-ink bg-aviana-surface px-6 py-5" data-testid="deposit-only-locked">
                    <div className="flex items-baseline gap-3">
                      <span className="font-figure text-4xl text-aviana-ink">${offering.deposit_usd}</span>
                      <span className="text-aviana-muted text-[13px]">non-refundable deposit today</span>
                    </div>
                    <p className="text-[13px] text-aviana-muted italic mt-2">Holly will invoice your remaining tuition separately once the final group size is confirmed.</p>
                  </div>
                </Section>
              );
            }
            return (
              <Section
                icon={CheckCircle2}
                title="How much would you like to pay today?"
                subtitle={
                  eligibleForSpecial
                    ? `Special tuition: $${effectiveTuition.toLocaleString()} (${offering.special_tuition_label || "hosting-studio rate"}). Balance is due ${balanceTimingLabel}.`
                    : `Total tuition: $${offering.price_usd.toLocaleString()}. Balance is due ${balanceTimingLabel}.`
                }
              >
                {eligibleForSpecial && (
                  <div className="mb-2 rounded-xl bg-aviana-gold/10 border border-aviana-gold/30 px-4 py-3" data-testid="special-tuition-applied">
                    <p className="text-[11px] tracking-[0.18em] uppercase text-aviana-goldDeep">Hosting-studio rate applied</p>
                    <p className="text-aviana-ink text-[14px] leading-[1.55] mt-1">
                      Because you confirmed you're affiliated with the hosting studio, your tuition is{" "}
                      <strong className="text-aviana-plum">${effectiveTuition.toLocaleString()}</strong>
                      {" "}<span className="text-aviana-muted">(standard: <span className="line-through">${offering.price_usd.toLocaleString()}</span>)</span>.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <PaymentChoice
                    selected={form.payment_type === "deposit"}
                    onClick={() => update({ payment_type: "deposit" })}
                    amount={`$${offering.deposit_usd}`}
                    label="Deposit today"
                    sub={`Balance of $${balanceLeft.toLocaleString()} due before training`}
                    testid="choose-deposit"
                  />
                  <PaymentChoice
                    selected={form.payment_type === "full"}
                    onClick={() => update({ payment_type: "full" })}
                    amount={`$${effectiveTuition.toLocaleString()}`}
                    label="Pay in full"
                    sub="Done — no balance to worry about"
                    testid="choose-full"
                  />
                </div>
              </Section>
            );
          })()}

          {/* Studio-pays-tuition note in lieu of payment chooser */}
          {offering.studio_pays_tuition && (
            <Section
              icon={CheckCircle2}
              title="Tuition is handled by the studio"
              subtitle={`${offering.exclusive_studio_name || "The hosting studio"} is paying Holly directly. You don't pay anything here on the website.`}
            >
              <div className="rounded-xl bg-aviana-gold/10 border border-aviana-gold/30 px-5 py-4" data-testid="studio-pays-tuition-note">
                <p className="text-aviana-ink text-[14px] leading-[1.65]">
                  Total tuition: <strong className="text-aviana-plum">${offering.price_usd?.toLocaleString()}</strong> — paid by your studio. Upon submitting this registration your spot is confirmed and you'll be auto-enrolled in the online <strong>Aerial Yoga Teacher Training Modules</strong> course so you can complete your certification work after the in-person dates.
                </p>
              </div>
            </Section>
          )}

          <div className="pt-4">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full !py-4 !text-[13px] tracking-[0.22em] uppercase flex items-center justify-center gap-3"
              data-testid="submit-registration"
            >
              {submitting
                ? "Saving your registration…"
                : offering.studio_pays_tuition
                ? <>Complete my registration <ArrowRight size={16} /></>
                : <>Continue to payment <ArrowRight size={16} /></>
              }
            </button>
            <p className="text-center text-[11px] text-aviana-muted mt-4">
              {offering.studio_pays_tuition
                ? "Your spot is confirmed the moment you submit. Online modules access is granted automatically."
                : "Next you'll choose how to pay: card, Venmo, or Zelle."
              }
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="bg-white border border-aviana-border rounded-[1.75rem] p-7 md:p-9">
      <header className="flex items-start gap-4 mb-6">
        <div className="w-10 h-10 rounded-full bg-aviana-plum/10 text-aviana-plum flex items-center justify-center flex-shrink-0">
          <Icon size={18} />
        </div>
        <div>
          <h2 className="font-display text-2xl md:text-3xl text-aviana-ink leading-tight">{title}</h2>
          {subtitle && <p className="mt-1 text-aviana-muted text-[14px] leading-[1.6]">{subtitle}</p>}
        </div>
      </header>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid sm:grid-cols-2 gap-5">{children}</div>;
}

function FormField({ label, name, value, onChange, type = "text", multiline = false, rows = 3, placeholder = "" }) {
  return (
    <label className="block" data-field={name}>
      <span className="overline-plum mb-2 block">{label}</span>
      {multiline ? (
        <textarea
          rows={rows}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-aviana-cream border border-aviana-border rounded-xl px-4 py-3 text-[14px] text-aviana-ink leading-[1.55] focus:outline-none focus:border-aviana-plum focus:ring-1 focus:ring-aviana-plum/30 placeholder:text-aviana-muted/60"
        />
      ) : (
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-aviana-cream border border-aviana-border rounded-xl px-4 py-3 text-[14px] text-aviana-ink focus:outline-none focus:border-aviana-plum focus:ring-1 focus:ring-aviana-plum/30 placeholder:text-aviana-muted/60"
        />
      )}
    </label>
  );
}

function YesNo({ field, label, hint, value, onChange, compact }) {
  return (
    <div data-field={field}>
      <p className={`text-aviana-ink font-medium ${compact ? "text-[14px]" : "text-[15px]"} leading-snug`}>{label}</p>
      {hint && !compact && <p className="text-aviana-muted text-[13px] leading-[1.65] mt-1.5">{hint}</p>}
      <div className="mt-3 flex gap-2">
        {[
          { v: true, l: "Yes" },
          { v: false, l: "No" },
        ].map((opt) => (
          <button
            key={String(opt.v)}
            type="button"
            onClick={() => onChange(opt.v)}
            className={`px-6 py-2 rounded-full text-[12px] tracking-[0.18em] uppercase border transition-colors ${
              value === opt.v
                ? "bg-aviana-ink text-aviana-cream border-aviana-ink"
                : "border-aviana-border text-aviana-inkSoft hover:border-aviana-plum"
            }`}
            data-testid={`${field}-${opt.l.toLowerCase()}`}
          >
            {opt.l}
          </button>
        ))}
      </div>
    </div>
  );
}

function PaymentChoice({ selected, onClick, amount, label, sub, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-5 rounded-2xl border transition-all ${
        selected ? "border-aviana-ink bg-aviana-cream" : "border-aviana-border hover:border-aviana-plum/40"
      }`}
    >
      <div className="font-figure text-3xl text-aviana-ink">{amount}</div>
      <div className="text-[12px] tracking-[0.18em] uppercase text-aviana-plum mt-1">{label}</div>
      <div className="text-[13px] text-aviana-muted leading-[1.55] mt-2">{sub}</div>
    </button>
  );
}
